import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import {
  Alert,
  Card,
  Filters,
  Page,
  PageHeader,
  Pagination,
  type FilterFieldSlot,
} from '@evogard/ui';
import { CHANGE_SUBJECTS, type ChangeSubject } from '@evogard/contract';
import { loadFailureMessage } from '@evogard/domain';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import { useListSearch, useSearchTerm, useClampedPage } from '../../lib/list-search.js';
import { MirrorStamp } from './common.js';
import {
  ChangesTable,
  fieldLabel,
  HistoryRefreshButton,
  SUBJECT_LABEL,
} from './history.js';

/**
 * O que mudou no SGA, campo a campo — boletos, associados e veículos
 * (14/10/2026).
 *
 * Responde "quem mudou o vencimento deste boleto, e de quanto para quanto", que
 * a ficha sozinha não sabe: ela mostra o valor de AGORA. A esteira acompanha os
 * campos que a casa pediu ao SGA (`CHANGE_FIELDS`) — o SGA só devolve a mudança
 * do que é pedido —, e a lista de campos do filtro é essa.
 */

/** Os campos que a esteira acompanha, por lista — o espelho de `CHANGE_FIELDS`. */
// @fornecedor — nomes de coluna do SGA
const TRACKED_FIELDS: Record<ChangeSubject, string[]> = {
  boleto: ['codigo_situacao', 'valor', 'data_pagamento', 'data_vencimento'],
  associado: ['codigo_situacao', 'email', 'telefone_celular'],
  veiculo: ['codigo_situacao', 'placa'],
};

export function ChangesScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  /* página, tamanho, busca e filtros na URL — o "voltar" devolve a lista como estava */
  const search = useSearch({ from: '/authenticated/erp/alteracoes' });
  const list = useListSearch(search);
  const { text, setText } = useSearchTerm(search.q, (q) => list.setFilters({ q }));
  const term = search.q ?? '';
  const applied = {
    subject: search.subject ?? '',
    field: search.field ?? '',
    from: search.from ?? '',
    until: search.until ?? '',
  };
  const { page, perPage } = list;

  const query = useQuery({
    queryKey: ['erp', 'alteracoes', tenantId, term, applied, page, perPage],
    queryFn: async () => {
      const r = await api.erp.searchChanges({
        body: {
          term: term.length >= 2 ? term : undefined,
          subject: (applied.subject || undefined) as ChangeSubject | undefined,
          field: applied.field || undefined,
          from: applied.from || undefined,
          until: applied.until || undefined,
          page,
          perPage,
        },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'as alterações'));
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const fieldOptions = [
    ...new Set(
      applied.subject
        ? TRACKED_FIELDS[applied.subject as ChangeSubject]
        : Object.values(TRACKED_FIELDS).flat(),
    ),
  ].map((f) => ({ value: f, label: fieldLabel(f) }));

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'subject',
      label: 'Registro',
      placeholder: 'Todos',
      options: CHANGE_SUBJECTS.map((s) => ({ value: s, label: SUBJECT_LABEL[s] })),
    },
    {
      type: 'selecao',
      name: 'field',
      label: 'Campo',
      placeholder: 'Todos',
      options: fieldOptions,
    },
    { type: 'intervalo', label: 'Alterado em', from: 'from', to: 'until' },
  ];

  const result = query.data;
  /* a página além do fim (link antigo, lista que encolheu) vai para a última */
  useClampedPage(page, perPage, result && 'total' in result ? result.total : undefined);
  const filtered = Boolean(term || Object.values(applied).some(Boolean));

  return (
    <Page measure="cheia">
      <PageHeader title="Alterações">
        O que mudou nos boletos, associados e veículos de {context?.currentTenant.nome},
        como o ERP registra.
      </PageHeader>

      <HistoryRefreshButton
        entity="alteracao"
        hint="traz as alterações registradas no SGA desde a última passagem"
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {(!query.isError || result) && (
        <Card
          title={result === undefined ? 'Carregando…' : `${result.total} alteração(ões)`}
          descricao={
            <>
              <MirrorStamp at={result?.syncedAt ?? null} /> · situação, valor, vencimento
              e pagamento do boleto; situação, e-mail e celular do associado; situação e
              placa do veículo
            </>
          }
          bleed
        >
          <ChangesTable
            items={result?.estado === 'lida' ? result.items : []}
            loading={query.isLoading}
            withRecord
            empty={
              filtered
                ? 'Nenhuma alteração com esses filtros.'
                : 'Nenhuma alteração no cadastro desta base ainda.'
            }
            search={{
              label: 'Pesquisar alterações',
              placeholder:
                'Nosso número, código (BO-, AS-, VE- ou do ERP) ou quem alterou',
              value: text,
              onChange: setText,
            }}
            toolbar={
              <Filters
                values={applied}
                fields={fields}
                onApply={(values) =>
                  list.setFilters({
                    subject: String(values.subject ?? '') || undefined,
                    field: String(values.field ?? '') || undefined,
                    from: String(values.from ?? '') || undefined,
                    until: String(values.until ?? '') || undefined,
                  })
                }
              />
            }
          />
          {result?.estado === 'lida' && (
            <Pagination
              page={result.page}
              perPage={result.perPage}
              total={result.total}
              onChange={list.setPage}
              onPerPageChange={list.setPerPage}
              label="alterações"
              className="border-t border-borda-suave px-5 py-4"
            />
          )}
        </Card>
      )}
    </Page>
  );
}
